import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { services } from '../data/services';
import './ServiceDetail.css';

const ServiceDetail = () => {
  const { serviceSlug } = useParams();
  const navigate = useNavigate();
  const service = services.find(s => s.slug === serviceSlug);

  useEffect(() => {
    // Redirect to services page if service not found
    if (!service) {
      navigate('/services');
    }
  }, [service, navigate]);

  if (!service) {
    return null;
  }

  // Find current service index and get adjacent services
  const currentIndex = services.findIndex(s => s.slug === serviceSlug);
  const prevService = currentIndex > 0 ? services[currentIndex - 1] : null;
  const nextService = currentIndex < services.length - 1 ? services[currentIndex + 1] : null;

  return (
    <div className="service-detail-page">
      <Helmet>
        <title>{service.title} - TB Construction | Greater Boston Area</title>
        <meta name="description" content={service.metaDescription} />
        <meta name="keywords" content={service.metaKeywords} />
        <link rel="canonical" href={`https://www.tbconstructionco.com/services/${service.slug}`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://www.tbconstructionco.com/services/${service.slug}`} />
        <meta property="og:title" content={`${service.title} - TB Construction`} />
        <meta property="og:description" content={service.metaDescription} />
        <meta property="og:image" content={`https://www.tbconstructionco.com${service.image}`} />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`https://www.tbconstructionco.com/services/${service.slug}`} />
        <meta property="twitter:title" content={`${service.title} - TB Construction`} />
        <meta property="twitter:description" content={service.metaDescription} />
        <meta property="twitter:image" content={`https://www.tbconstructionco.com${service.image}`} />
        
        {/* Schema.org BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "@id": `https://www.tbconstructionco.com/services/${service.slug}#breadcrumb`,
            "name": `${service.title} Breadcrumb`,
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": {
                  "@type": "WebPage",
                  "@id": "https://www.tbconstructionco.com/",
                  "name": "Home"
                }
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Services",
                "item": {
                  "@type": "WebPage",
                  "@id": "https://www.tbconstructionco.com/services",
                  "name": "Services"
                }
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": service.title,
                "item": {
                  "@type": "WebPage",
                  "@id": `https://www.tbconstructionco.com/services/${service.slug}`,
                  "name": service.title
                }
              }
            ]
          })}
        </script>
        
        {/* Schema.org for Service */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": service.title,
            "description": service.metaDescription,
            "image": `https://www.tbconstructionco.com${service.image}`,
            "provider": {
              "@type": "LocalBusiness",
              "name": "TB Construction",
              "url": "https://www.tbconstructionco.com",
              "telephone": "+1-617-412-6800",
              "email": "info@tbconstructionco.com",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Boston",
                "addressRegion": "MA",
                "addressCountry": "US"
              }
            },
            "areaServed": {
              "@type": "State",
              "name": "Massachusetts"
            },
            "serviceType": service.title,
            "offers": {
              "@type": "Offer",
              "description": service.description
            }
          })}
        </script>
      </Helmet>
      
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb-nav" aria-label="Breadcrumb">
        <div className="container">
          <ol className="breadcrumb-list">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-separator">/</li>
            <li className="breadcrumb-item">
              <Link to="/services">Services</Link>
            </li>
            <li className="breadcrumb-separator">/</li>
            <li className="breadcrumb-item active" aria-current="page">
              {service.title}
            </li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section 
        className="service-detail-hero"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${service.image})`
        }}
      >
        <div className="container">
          <Link to="/services" className="back-link">
            ← Back to All Services
          </Link>
          <div className="service-hero-content">
            <img src={service.icon} alt={service.title} className="service-hero-icon" />
            <h1 className="service-detail-title">{service.title}</h1>
            <p className="service-detail-subtitle">{service.description}</p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <article className="service-detail-content">
        <div className="container">
          <div className="service-detail-wrapper">
            <div className="service-detail-main">
              <div className="service-detail-section">
                <h2 className="section-heading">What We Provide</h2>
                <p className="service-intro-text">
                  {service.metaDescription}
                </p>
                <div className="service-details-grid">
                  {service.details.map((detail, idx) => (
                    <div key={idx} className="detail-item">
                      {detail}
                    </div>
                  ))}
                </div>
              </div>

              <div className="service-detail-section">
                <h2 className="section-heading">Why Choose TB Construction</h2>
                <p className="service-description-text">
                  With over 30 years of experience in the Greater Boston Area, TB Construction brings 
                  expertise and precision to every {service.title.toLowerCase()} project. We work with 
                  approved architectural construction documents to provide accurate proposals, schedules, 
                  and pricing for both residential and commercial applications.
                </p>
                <p className="service-description-text">
                  Our team specializes in {service.title.toLowerCase()} services, ensuring quality workmanship 
                  and attention to detail on every project. Contact us today at <a href="tel:6174126800">(617) 412-6800</a> or 
                  <a href="mailto:info@tbconstructionco.com"> info@tbconstructionco.com</a> to discuss your {service.title.toLowerCase()} needs.
                </p>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="service-detail-sidebar">
              <div className="sidebar-card">
                <h3>Other Services</h3>
                <ul className="other-services-list">
                  {services
                    .filter(s => s.slug !== serviceSlug)
                    .slice(0, 5)
                    .map(otherService => (
                      <li key={otherService.id}>
                        <Link to={`/services/${otherService.slug}`}>
                          {otherService.title}
                        </Link>
                      </li>
                    ))}
                </ul>
              </div>
              <div className="sidebar-card">
                <h3>Get Started</h3>
                <p>Ready to discuss your project?</p>
                <Link to="/contact" className="btn-primary btn-block">
                  Get Your Proposal
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </article>

      {/* Navigation */}
      <section className="service-navigation">
        <div className="container">
          <div className="service-nav-links">
            {prevService && (
              <Link to={`/services/${prevService.slug}`} className="nav-link prev">
                <span className="nav-arrow">←</span>
                <div className="nav-content">
                  <span className="nav-label">Previous Service</span>
                  <span className="nav-title">{prevService.title}</span>
                </div>
              </Link>
            )}
            {nextService && (
              <Link to={`/services/${nextService.slug}`} className="nav-link next">
                <div className="nav-content">
                  <span className="nav-label">Next Service</span>
                  <span className="nav-title">{nextService.title}</span>
                </div>
                <span className="nav-arrow">→</span>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="service-detail-cta">
        <div className="container">
          <h2>Ready to Start Your {service.title} Project?</h2>
          <p>Contact us today to discuss your needs and get a detailed proposal</p>
          <Link to="/contact" className="btn-primary btn-large">
            Get Your Proposal
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;

